import * as THREE from 'three';
import store from '../../redux/store';
import {
  selected_object,
  setPushIntersectedObject,
} from '../../redux/Slice/gridSlice';
import { selectObjectInScene } from '../../pages/Dashboard/GridPage';
import {
  startAnimation,
  stopAnimation,
  pauseAnimation,
} from './animationControls';
import { setLifeTimeout, clearLifeTimeout } from './lifeUtils';

// ImprovedNoise implementation (from Three.js)
class ImprovedNoise {
  constructor() {
    const p = [];
    for (let i = 0; i < 256; i++) {
      p[i] = Math.floor(Math.random() * 256);
    }
    this.p = new Uint8Array(512);
    for (let i = 0; i < 512; i++) {
      this.p[i] = p[i & 255];
    }
  }

  noise(x, y, z) {
    const p = this.p;
    const X = Math.floor(x) & 255;
    const Y = Math.floor(y) & 255;
    const Z = Math.floor(z) & 255;
    x -= Math.floor(x);
    y -= Math.floor(y);
    z -= Math.floor(z);
    const u = this.fade(x);
    const v = this.fade(y);
    const w = this.fade(z);
    const A = p[X] + Y;
    const AA = p[A] + Z;
    const AB = p[A + 1] + Z;
    const B = p[X + 1] + Y;
    const BA = p[B] + Z;
    const BB = p[B + 1] + Z;

    return this.lerp(
      w,
      this.lerp(
        v,
        this.lerp(u, this.grad(p[AA], x, y, z), this.grad(p[BA], x - 1, y, z)),
        this.lerp(
          u,
          this.grad(p[AB], x, y - 1, z),
          this.grad(p[BB], x - 1, y - 1, z)
        )
      ),
      this.lerp(
        v,
        this.lerp(
          u,
          this.grad(p[AA + 1], x, y, z - 1),
          this.grad(p[BA + 1], x - 1, y, z - 1)
        ),
        this.lerp(
          u,
          this.grad(p[AB + 1], x, y - 1, z - 1),
          this.grad(p[BB + 1], x - 1, y - 1, z - 1)
        )
      )
    );
  }

  fade(t) {
    return t * t * t * (t * (t * 6 - 15) + 10);
  }

  lerp(t, a, b) {
    return a + t * (b - a);
  }

  grad(hash, x, y, z) {
    const h = hash & 15;
    const u = h < 8 ? x : y;
    const v = h < 4 ? y : h === 12 || h === 14 ? x : z;
    return ((h & 1) === 0 ? u : -u) + ((h & 2) === 0 ? v : -v);
  }
}

// Generate height data using ImprovedNoise with mountain-like characteristics
const generateHeight = (width, height, seed = 42) => {
  const size = width * height;
  const data = new Uint8Array(size);
  const perlin = new ImprovedNoise();
  const z = seed; // Use fixed seed instead of random

  let quality = 1;

  // Multiple octaves for more realistic mountain terrain
  for (let j = 0; j < 4; j++) {
    for (let i = 0; i < size; i++) {
      const x = i % width;
      const y = ~~(i / width);
      data[i] += Math.abs(
        perlin.noise(x / quality, y / quality, z) * quality * 1.75
      );
    }
    quality *= 5;
  }

  return data;
};

// Parse intensity input (accepts numbers or 'Low'|'Medium'|'High')
const parseIntensity = (value) => {
  if (value === undefined || value === null) return 1;
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  if (typeof value === 'string') {
    const s = value.trim().toLowerCase();
    if (s === 'low') return 0.6;
    if (s === 'medium') return 1;
    if (s === 'high') return 1.6;
    const n = parseFloat(s);
    if (!Number.isNaN(n)) return n;
  }
  return 1;
};

// Generate displacement texture from height data (grayscale)
const generateDisplacementTexture = (data, width, height) => {
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d', { alpha: false });
  const image = context.createImageData(width, height);
  const imageData = image.data;

  // Convert height data to grayscale displacement map
  for (let i = 0, j = 0, l = imageData.length; i < l; i += 4, j++) {
    const value = data[j];
    imageData[i] = value; // R
    imageData[i + 1] = value; // G
    imageData[i + 2] = value; // B
    imageData[i + 3] = 255; // A
  }

  context.putImageData(image, 0, 0);
  return canvas;
};

// Generate color texture with altitude-based coloring (mountain-like)
const generateColorTexture = (
  data,
  width,
  height,
  color = '#ffffff',
  intensity = 1
) => {
  // Parse color input to RGB values
  const colorObj = new THREE.Color(color);
  const colorR = Math.round(colorObj.r * 255);
  const colorG = Math.round(colorObj.g * 255);
  const colorB = Math.round(colorObj.b * 255);
  // Normalize intensity (support string presets) and clamp to sensible range
  intensity = parseIntensity(intensity);
  if (intensity < 0) intensity = 0;
  const vector3 = new THREE.Vector3(0, 0, 0);
  const sun = new THREE.Vector3(1, 1, 1);
  sun.normalize();

  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  let context = canvas.getContext('2d', { alpha: false });
  context.fillStyle = '#000';
  context.fillRect(0, 0, width, height);

  let image = context.getImageData(0, 0, canvas.width, canvas.height);
  let imageData = image.data;

  // Calculate min and max for altitude-based coloring
  let minHeight = 255;
  let maxHeight = 0;
  for (let i = 0; i < data.length; i++) {
    minHeight = Math.min(minHeight, data[i]);
    maxHeight = Math.max(maxHeight, data[i]);
  }

  for (let i = 0, j = 0, l = imageData.length; i < l; i += 4, j++) {
    // Calculate normal for lighting
    vector3.x = data[j - 2] - data[j + 2];
    vector3.y = 2;
    vector3.z = data[j - width * 2] - data[j + width * 2];
    vector3.normalize();

    const shade = vector3.dot(sun);

    // Altitude-based coloring (mountain-like)
    const normalizedHeight = (data[j] - minHeight) / (maxHeight - minHeight);

    let r, g, b;
    // Apply color tint based on altitude
    if (normalizedHeight < 0.3) {
      // Very dark soil - tinted with input color
      r = Math.round((40 + shade * 25) * (colorR / 255));
      g = Math.round((25 + shade * 20) * (colorG / 255));
      b = Math.round((15 + shade * 15) * (colorB / 255));
    } else if (normalizedHeight < 0.6) {
      // Dark brown dirt - tinted with input color
      r = Math.round((70 + shade * 30) * (colorR / 255));
      g = Math.round((45 + shade * 25) * (colorG / 255));
      b = Math.round((30 + shade * 20) * (colorB / 255));
    } else if (normalizedHeight < 0.8) {
      // Dark rocky brown - tinted with input color
      r = Math.round((95 + shade * 30) * (colorR / 255));
      g = Math.round((70 + shade * 25) * (colorG / 255));
      b = Math.round((50 + shade * 20) * (colorB / 255));
    } else {
      // Highest peaks - tinted with input color
      r = Math.round((140 + shade * 20) * (colorR / 255));
      g = Math.round((120 + shade * 15) * (colorG / 255));
      b = Math.round((100 + shade * 15) * (colorB / 255));
    }

    // Apply intensity scaling and clamp to [0,255]
    r = Math.min(255, Math.max(0, Math.round(r * intensity)));
    g = Math.min(255, Math.max(0, Math.round(g * intensity)));
    b = Math.min(255, Math.max(0, Math.round(b * intensity)));

    imageData[i] = r;
    imageData[i + 1] = g;
    imageData[i + 2] = b;
    imageData[i + 3] = 255;
  }

  context.putImageData(image, 0, 0);

  // Scaled 4x for better quality
  const canvasScaled = document.createElement('canvas');
  canvasScaled.width = width * 4;
  canvasScaled.height = height * 4;

  context = canvasScaled.getContext('2d');
  context.scale(4, 4);
  context.drawImage(canvas, 0, 0);

  // No random noise added - colors stay consistent based on altitude

  return canvasScaled;
};

const handleTerrain = (scene, particle) => {
  if (!scene) return;

  // Use dynamic properties with proper mapping from ParticleComponent
  const terrainWidth = particle?.terrainWidth || 256;
  const terrainHeight = particle?.terrainHeight || 256;
  const terrainSize = particle?.terrainSize || 25;

  const worldWidth = terrainWidth;
  const worldDepth = terrainHeight;

  // Generate height data
  const data = generateHeight(worldWidth, worldDepth);

  // Create high-resolution geometry for better displacement
  const geometry = new THREE.PlaneGeometry(
    terrainSize,
    terrainSize,
    worldWidth - 10,
    worldDepth - 1
  );
  geometry.rotateX(-Math.PI / 2);

  // Generate displacement texture (grayscale height map)
  const displacementCanvas = generateDisplacementTexture(
    data,
    worldWidth,
    worldDepth
  );
  const displacementTexture = new THREE.CanvasTexture(displacementCanvas);
  displacementTexture.wrapS = THREE.ClampToEdgeWrapping;
  displacementTexture.wrapT = THREE.ClampToEdgeWrapping;

  // Generate color texture with mountain-like coloring (apply particle color + intensity)
  const colorCanvas = generateColorTexture(
    data,
    worldWidth,
    worldDepth,
    particle?.color || '#ffffff',
    parseIntensity(particle?.intensity)
  );
  const colorTexture = new THREE.CanvasTexture(colorCanvas);
  colorTexture.wrapS = THREE.ClampToEdgeWrapping;
  colorTexture.wrapT = THREE.ClampToEdgeWrapping;
  colorTexture.colorSpace = THREE.SRGBColorSpace;

  // Create material with displacement map
  const material = new THREE.MeshStandardMaterial({
    map: colorTexture,
    displacementMap: displacementTexture,
    displacementScale: 20, // Adjust for mountain height
    side: THREE.DoubleSide,
    transparent: particle?.opacity !== undefined && particle.opacity < 1,
    opacity: particle?.opacity !== undefined ? particle.opacity : 1,
  });

  const terrain = new THREE.Mesh(geometry, material);
  terrain.position.y = 0;
  terrain.receiveShadow = true;
  terrain.castShadow = true;
  terrain.deleteType = 'particle';
  terrain.objType = 'terrain';

  const uniqueId = `terrain_${Date.now()}`;

  // Set the name based on particle data or generate unique ID
  if (particle && particle.id) {
    terrain.name = particle.id;
  } else if (particle && particle._id) {
    terrain.name = particle._id;
  } else {
    terrain.name = uniqueId;
  }

  terrain.displayName = 'Terrain'; // For UI display
  terrain.url = uniqueId;
  terrain.asset_file_name = 'terrain';
  terrain.file_name = 'terrain';
  terrain.mesh_code = 'terrain';
  terrain.glb_type = 'shapes';
  terrain.createdAt = new Date().toISOString();
  terrain.userData = {
    material_settings: {
      shader: 'Standard',
    },
    terrainHeight: terrainHeight,
    terrainWidth: terrainWidth,
    terrainSize: terrainSize,
    opacity: particle?.opacity !== undefined ? particle.opacity : 1.0,
    color: particle?.color !== undefined ? particle.color : '#ffffff',
    intensity: particle?.intensity !== undefined ? particle.intensity : 1,
    heightData: data,
    life: particle?.life || 120,
    cleanup: () => {
      if (terrain.geometry) {
        terrain.geometry.dispose();
      }
      if (terrain.material) {
        if (terrain.material.map) {
          terrain.material.map.dispose();
        }
        if (terrain.material.displacementMap) {
          terrain.material.displacementMap.dispose();
        }
        terrain.material.dispose();
      }
    },
  };

  // Ensure terrain is visible and selectable
  terrain.visible = true;
  terrain.matrixAutoUpdate = true;
  terrain.updateMatrix();

  // Add terrain to scene
  scene.add(terrain);
  store.dispatch(setPushIntersectedObject(terrain));

  selectObjectInScene(terrain.name);
  store.dispatch(selected_object(terrain));

  // Manage life timeout for terrain (hide after life seconds)
  try {
    if (!particle?.isStopped) {
      setLifeTimeout(
        terrain,
        typeof terrain.userData.life === 'number'
          ? terrain.userData.life
          : parseFloat(terrain.userData.life) || 0
      );
    } else {
      clearLifeTimeout(terrain);
    }
  } catch (e) {
    // ignore
  }
};

const updateTerrain = (scene, terrainSystem, newParticleData) => {
  if (!scene || !terrainSystem || terrainSystem.objType !== 'terrain') {
    console.warn('Invalid terrain system or scene provided for update');
    return;
  }

  // Get current configuration
  const currentConfig = terrainSystem.userData || {};

  // Handle animation state changes
  if (
    typeof newParticleData?.isPlaying !== 'undefined' ||
    typeof newParticleData?.isStopped !== 'undefined'
  ) {
    const isPlaying =
      newParticleData?.isPlaying ?? currentConfig.isPlaying ?? true;
    const isStopped =
      newParticleData?.isStopped ?? currentConfig.isStopped ?? false;

    if (isStopped) {
      stopAnimation(terrainSystem, terrainSystem.name);
    } else if (isPlaying) {
      const animateFunction = terrainSystem.userData.animateFunction;
      if (animateFunction) {
        startAnimation(terrainSystem, animateFunction, terrainSystem.name);
      }
    } else {
      pauseAnimation(terrainSystem, terrainSystem.name);
    }
  }

  // Merge current config with new particle data
  const updatedConfig = {
    terrainHeight:
      newParticleData?.terrainHeight !== undefined
        ? newParticleData.terrainHeight
        : currentConfig.terrainHeight || 256,
    terrainWidth:
      newParticleData?.terrainWidth !== undefined
        ? newParticleData.terrainWidth
        : currentConfig.terrainWidth || 256,
    terrainSize:
      newParticleData?.terrainSize !== undefined
        ? newParticleData.terrainSize
        : currentConfig.terrainSize || 25,
    opacity:
      newParticleData?.opacity !== undefined
        ? newParticleData.opacity
        : currentConfig.opacity !== undefined
          ? currentConfig.opacity
          : 1.0,
    color:
      newParticleData?.color !== undefined
        ? newParticleData.color
        : currentConfig.color !== undefined
          ? currentConfig.color
          : '#ffffff',
    intensity:
      newParticleData?.intensity !== undefined
        ? newParticleData.intensity
        : currentConfig.intensity !== undefined
          ? currentConfig.intensity
          : 1,
    life: newParticleData?.life ?? currentConfig.life ?? 0,
  };

  // Check if geometry needs to be recreated
  const needsGeometryUpdate =
    updatedConfig.terrainHeight !== currentConfig.terrainHeight ||
    updatedConfig.terrainWidth !== currentConfig.terrainWidth ||
    updatedConfig.terrainSize !== currentConfig.terrainSize;

  if (needsGeometryUpdate) {
    

    const worldWidth = updatedConfig.terrainWidth;
    const worldDepth = updatedConfig.terrainHeight;

    // Generate new height data
    const data = generateHeight(worldWidth, worldDepth);

    // Dispose old geometry and textures
    if (terrainSystem.geometry) {
      terrainSystem.geometry.dispose();
    }
    if (terrainSystem.material) {
      if (terrainSystem.material.map) {
        terrainSystem.material.map.dispose();
      }
      if (terrainSystem.material.displacementMap) {
        terrainSystem.material.displacementMap.dispose();
      }
    }

    // Create new geometry
    const newGeometry = new THREE.PlaneGeometry(
      updatedConfig.terrainSize,
      updatedConfig.terrainSize,
      worldWidth - 1,
      worldDepth - 1
    );
    newGeometry.rotateX(-Math.PI / 2);

    // Generate new displacement texture
    const displacementCanvas = generateDisplacementTexture(
      data,
      worldWidth,
      worldDepth
    );
    const displacementTexture = new THREE.CanvasTexture(displacementCanvas);
    displacementTexture.wrapS = THREE.ClampToEdgeWrapping;
    displacementTexture.wrapT = THREE.ClampToEdgeWrapping;

    // Generate new color texture (apply color + intensity)
    const colorCanvas = generateColorTexture(
      data,
      worldWidth,
      worldDepth,
      updatedConfig.color,
      parseIntensity(updatedConfig.intensity)
    );
    const colorTexture = new THREE.CanvasTexture(colorCanvas);
    colorTexture.wrapS = THREE.ClampToEdgeWrapping;
    colorTexture.wrapT = THREE.ClampToEdgeWrapping;
    colorTexture.colorSpace = THREE.SRGBColorSpace;

    // Update terrain geometry and material
    terrainSystem.geometry = newGeometry;
    if (terrainSystem.material) {
      terrainSystem.material.map = colorTexture;
      terrainSystem.material.displacementMap = displacementTexture;
      terrainSystem.material.displacementScale = 20;
      terrainSystem.material.transparent = updatedConfig.opacity < 1;
      terrainSystem.material.opacity = updatedConfig.opacity;
      terrainSystem.material.needsUpdate = true;
    }

    // Store height data
    updatedConfig.heightData = data;
  } else {
    // Update opacity and color even if geometry doesn't change
    if (terrainSystem.material) {
      if (newParticleData?.opacity !== undefined) {
        terrainSystem.material.transparent = updatedConfig.opacity < 1;
        terrainSystem.material.opacity = updatedConfig.opacity;
        terrainSystem.material.needsUpdate = true;
      }

      // If color or intensity changed, regenerate the color texture
      if (
        (newParticleData?.color !== undefined &&
          currentConfig.color !== updatedConfig.color) ||
        (newParticleData?.intensity !== undefined &&
          currentConfig.intensity !== updatedConfig.intensity)
      ) {
        const worldWidth = updatedConfig.terrainWidth;
        const worldDepth = updatedConfig.terrainHeight;

        // Regenerate color texture with new color/intensity
        const colorCanvas = generateColorTexture(
          currentConfig.heightData,
          worldWidth,
          worldDepth,
          updatedConfig.color,
          parseIntensity(updatedConfig.intensity)
        );
        const colorTexture = new THREE.CanvasTexture(colorCanvas);
        colorTexture.wrapS = THREE.ClampToEdgeWrapping;
        colorTexture.wrapT = THREE.ClampToEdgeWrapping;
        colorTexture.colorSpace = THREE.SRGBColorSpace;

        // Dispose old color texture
        if (terrainSystem.material.map) {
          terrainSystem.material.map.dispose();
        }

        // Apply new color texture
        terrainSystem.material.map = colorTexture;
        terrainSystem.material.needsUpdate = true;
      }
    }
  }

  // Update transforms if provided
  if (newParticleData?.position) {
    terrainSystem.position.set(
      newParticleData.position.x !== undefined
        ? newParticleData.position.x
        : terrainSystem.position.x,
      newParticleData.position.y !== undefined
        ? newParticleData.position.y
        : terrainSystem.position.y,
      newParticleData.position.z !== undefined
        ? newParticleData.position.z
        : terrainSystem.position.z
    );
  }
  if (newParticleData?.rotation) {
    terrainSystem.rotation.set(
      newParticleData.rotation.x !== undefined
        ? newParticleData.rotation.x
        : terrainSystem.rotation.x,
      newParticleData.rotation.y !== undefined
        ? newParticleData.rotation.y
        : terrainSystem.rotation.y,
      newParticleData.rotation.z !== undefined
        ? newParticleData.rotation.z
        : terrainSystem.rotation.z
    );
  }
  if (newParticleData?.scale) {
    terrainSystem.scale.set(
      newParticleData.scale.x !== undefined
        ? newParticleData.scale.x
        : terrainSystem.scale.x,
      newParticleData.scale.y !== undefined
        ? newParticleData.scale.y
        : terrainSystem.scale.y,
      newParticleData.scale.z !== undefined
        ? newParticleData.scale.z
        : terrainSystem.scale.z
    );
  }

  // Update userData with new configuration
  terrainSystem.userData = {
    ...terrainSystem.userData,
    ...updatedConfig,
    material_settings: {
      shader: 'Standard',
    },
  };

  // Update system metadata if ID changed
  if (newParticleData?.id && newParticleData.id !== terrainSystem.name) {
    terrainSystem.name = newParticleData.id;
    terrainSystem.url = newParticleData.id;
  }

  // Update selections and transform controls
  store.dispatch(setPushIntersectedObject(terrainSystem));
  store.dispatch(selected_object(terrainSystem));
  selectObjectInScene(terrainSystem.name);

  // Manage life timeout: clear previous then set new if provided
  try {
    clearLifeTimeout(terrainSystem);
    const lifeVal = updatedConfig?.life ?? terrainSystem.userData?.life;
    const lifeSeconds =
      typeof lifeVal === 'number' ? lifeVal : parseFloat(lifeVal);
    if (!isNaN(lifeSeconds) && lifeSeconds > 0) {
      setLifeTimeout(terrainSystem, lifeSeconds);
      terrainSystem.visible = true;
      if (terrainSystem.userData) {
        terrainSystem.userData.isStopped = false;
        terrainSystem.userData.isPlaying = true;
      }
    }
  } catch (e) {
    // ignore
  }

  
  return terrainSystem;
};

// Helper function to find terrain system by name/id in scene
const findTerrainSystem = (scene, nameOrId) => {
  return scene.children.find(
    (child) =>
      (child.name === nameOrId || child.url === nameOrId) &&
      child.objType === 'terrain'
  );
};

// Convenience function to update terrain by name
const updateTerrainByName = (scene, nameOrId, newParticleData) => {
  const terrainSystem = findTerrainSystem(scene, nameOrId);
  if (terrainSystem) {
    return updateTerrain(scene, terrainSystem, newParticleData);
  } else {
    console.warn(
      `Terrain system with name/id "${nameOrId}" not found in scene`
    );
    return null;
  }
};

export default handleTerrain;
export { updateTerrain, updateTerrainByName };
